import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds for regular requests
});

// Separate API instance for long-running operations like document uploads
export const longRunningApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes for document processing
});

// Add auth interceptor to both instances
const addAuthInterceptor = (apiInstance: typeof api) => {
  apiInstance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Paths where a 401 is an expected inline-error case, NOT a
  // "your session expired, go back to /login" signal. Auto-redirecting
  // any of these would wipe the page state (form values, callback
  // params) mid-error-handler and hide the real reason from the user.
  //
  // Includes Risk #4 from the Entra integration plan: every /auth/*
  // endpoint the SPA can hit anonymously must be excluded, so the
  // LoginPage and AuthCallback can render their own error banner.
  const AUTH_401_EXCLUSIONS = [
    '/auth/login',
    '/auth/check-domain',
    '/auth/validate',
    '/auth/internal/login',
    '/auth/external/login',
    '/auth/internal/callback',
    '/auth/external/msal-callback',
  ];

  apiInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const url: string = error.config?.url || '';
      const isExcludedAuthPath = AUTH_401_EXCLUSIONS.some((p) => url.includes(p));
      if (error.response?.status === 401 && !isExcludedAuthPath) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );
};

// Apply interceptors to both instances
addAuthInterceptor(api);
addAuthInterceptor(longRunningApi);

export default api;
